import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import { authApi } from '../../api/auth';
import { usersApi } from '../../api/users';
import { useLanguage } from '../../i18n/LanguageContext';
import { useSound } from '../../context/SoundContext';
import { useMusic } from '../../context/MusicContext';
import { useCookieConsent } from '../../context/CookieConsentContext';

// Normalize wallet: DB/API can return wallet_address (snake) or walletAddress (camel)
const getAssociatedWallet = (u) => (u?.wallet_address || u?.walletAddress || '').trim() || null;

const SettingsTab = ({ user, setUser, connectWallet, disconnectWallet, account, isConnected }) => {
  const navigate = useNavigate();
  const { language: appLanguage, setLanguage: setAppLanguage, t } = useLanguage();
  const { enabled: soundsEnabled, setEnabled: setSoundsEnabled, playSound } = useSound();
  const { enabled: musicEnabled, setEnabled: setMusicEnabled, volume: musicVolume, setVolume: setMusicVolume, volumePercent: musicVolumePercent } = useMusic();
  const { openPreferences: openCookiePreferences } = useCookieConsent();
  const [language, setLanguage] = useState(user?.language || appLanguage || 'en');
  const [tutorialCompleted, setTutorialCompleted] = useState(user?.tutorial_completed || false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [savingWallet, setSavingWallet] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [justConnected, setJustConnected] = useState(false);
  const [walletMismatch, setWalletMismatch] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });

  const associatedWallet = getAssociatedWallet(user);
  const profileUserId = user?.isAdmin ? (user?.linkedUserId ?? user?.id) : user?.id;

  const handleLogout = async () => {
    try {
      await authApi.logout();
      window.dispatchEvent(new CustomEvent('dios10:userLoggedOut'));
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
      // Force logout even if API call fails
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.dispatchEvent(new CustomEvent('dios10:userLoggedOut'));
      navigate('/login');
    }
  };

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'it', name: 'Italiano' },
    { code: 'es', name: 'Español' },
    { code: 'fr', name: 'Français' },
    { code: 'de', name: 'Deutsch' },
    { code: 'pt', name: 'Português' },
    { code: 'tr', name: 'Türkçe' }
  ];

  useEffect(() => {
    if (appLanguage) {
      setLanguage(appLanguage);
    }
  }, [appLanguage]);

  const handleConnectWallet = async () => {
    try {
      setConnecting(true);
      setJustConnected(true);
      const result = await connectWallet();
      
      // Se la connessione fallisce silenziosamente (es. utente rifiuta), non mostrare alert
      if (result && !result.success) {
        // Solo mostra alert per errori inaspettati, non per rifiuti dell'utente
        const message = result.message || result.error || '';
        if (message && !message.includes('rejected') && !message.includes('User rejected') && !message.includes('not installed')) {
          console.warn('Wallet connection failed:', message);
        }
        setJustConnected(false);
      }
    } catch (error) {
      // Non mostrare alert per errori comuni
      const errorMessage = error.message || 'Unknown error';
      if (!errorMessage.includes('rejected') && !errorMessage.includes('User rejected') && !errorMessage.includes('MetaMask') && !errorMessage.includes('not installed')) {
        console.error('Error connecting wallet:', error);
        // Mostra alert solo per errori inaspettati
        alert(t('settingsExtra.walletConnectError', { message: errorMessage }));
      }
      setJustConnected(false);
    } finally {
      setConnecting(false);
    }
  };

  // Mostra il modal quando il wallet viene connesso dopo aver cliccato Connect
  useEffect(() => {
    if (justConnected && isConnected && account && !associatedWallet) {
      setShowWalletModal(true);
      setJustConnected(false);
    }
  }, [justConnected, isConnected, account, associatedWallet]);

  // Verifica che il wallet connesso corrisponda a quello associato
  useEffect(() => {
    if (isConnected && account && associatedWallet) {
      const connectedAddress = account.toLowerCase();
      const associatedAddress = associatedWallet.toLowerCase();
      
      if (connectedAddress !== associatedAddress) {
        setWalletMismatch(true);
        // Mostra un avviso quando il wallet viene connesso e non corrisponde
        if (justConnected) {
          alert(`⚠️ ${t('settingsExtra.walletMismatchBody')}`);
        }
      } else {
        setWalletMismatch(false);
      }
    } else {
      setWalletMismatch(false);
    }
  }, [isConnected, account, associatedWallet, justConnected, t]);

  const handleSaveWallet = async () => {
    const walletUserId = user?.isAdmin ? (user?.linkedUserId ?? user?.id) : user?.id;
    if (!account || !walletUserId) return;

    try {
      setSavingWallet(true);
      const response = await usersApi.updateWallet(walletUserId, account);
      if (response?.success) {
        // Aggiorna l'utente: sia wallet_address (DB) che walletAddress (auth) per coerenza
        const updatedUser = {
          ...user,
          wallet_address: account,
          walletAddress: account
        };
        // Se il backend restituisce l'utente aggiornato, usalo mantenendo entrambe le chiavi wallet
        if (response?.user && response.user.wallet_address) {
          updatedUser.wallet_address = response.user.wallet_address;
          updatedUser.walletAddress = response.user.wallet_address;
        }
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        setShowWalletModal(false);
        alert(t('settingsExtra.walletSaveSuccess'));
      } else {
        throw new Error(response?.error || 'Failed to save wallet');
      }
    } catch (error) {
      console.error('Error saving wallet:', error);
      alert(t('settingsExtra.walletSaveError', { message: error.message }));
    } finally {
      setSavingWallet(false);
    }
  };

  const handleSkipSaveWallet = () => {
    setShowWalletModal(false);
  };

  const handleSaveSettings = async () => {
    if (!user?.id) return;
    setSavingSettings(true);
    try {
      await Promise.all([
        usersApi.updateLanguage(user.id, language),
        usersApi.updateTutorialCompleted(user.id, tutorialCompleted)
      ]);
      const updatedUser = {
        ...user,
        language,
        tutorial_completed: tutorialCompleted
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      setAppLanguage(language);
      alert(t('settings.saveSuccess'));
    } catch (error) {
      console.error('Error saving settings:', error);
      alert(t('settings.saveFailed'));
    } finally {
      setSavingSettings(false);
    }
  };

  const handlePasswordFieldChange = (field, value) => {
    setPasswordForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePassword = async () => {
    if (!profileUserId) return;

    const { currentPassword, newPassword, confirmNewPassword } = passwordForm;

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      alert(t('settings.passwordFillAllFields'));
      return;
    }

    if (newPassword.length < 8) {
      alert(t('settings.passwordMinLength'));
      return;
    }

    if (newPassword !== confirmNewPassword) {
      alert(t('settings.passwordMismatch'));
      return;
    }

    if (currentPassword === newPassword) {
      alert(t('settings.passwordMustDiffer'));
      return;
    }

    try {
      setSavingPassword(true);
      await usersApi.updatePassword(profileUserId, currentPassword, newPassword, confirmNewPassword);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      });
      alert(t('settings.passwordChangeSuccess'));
    } catch (error) {
      console.error('Error changing password:', error);
      alert(error.message || t('settings.passwordChangeFailed'));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <>
      <div className="football-card glass-football p-6">
        <h2 className="text-2xl font-bold text-[#1e293b] mb-6">{t('settings.title')}</h2>
        <div className="space-y-6">
          {/* Wallet Connection Section */}
          <div className="border-b border-gray-700 pb-6">
            <h3 className="text-lg font-semibold text-[#1e293b] mb-4">{t('settings.walletConnection')}</h3>
            
            {/* Mostra wallet associato se presente */}
            {associatedWallet && (
              <div className="space-y-2 mb-4">
                <p className="text-gray-300 text-sm">{t('settingsExtra.walletAssociatedLabel')}</p>
                <div className="bg-gray-800 p-3 rounded-lg">
                  <p className="text-yellow-400 font-mono text-sm break-all">{associatedWallet}</p>
                </div>
              </div>
            )}

            {/* Avviso se il wallet connesso non corrisponde a quello associato + pulsante Disconnetti */}
            {walletMismatch && (
              <div className="mb-4 p-4 bg-red-500/20 border-2 border-red-500 rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="text-red-400 text-xl">⚠️</div>
                  <div className="flex-1">
                    <p className="text-red-400 font-semibold mb-1">{t('settingsExtra.walletMismatchTitle')}</p>
                    <p className="text-gray-300 text-sm">
                      {t('settingsExtra.walletMismatchBody')}
                    </p>
                    <p className="text-gray-400 text-xs mt-2">
                      {t('settingsExtra.walletConnected')}: <span className="font-mono">{account}</span>
                    </p>
                    {disconnectWallet && (
                      <button
                        type="button"
                        onClick={() => disconnectWallet(false)}
                        className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-500 text-white font-medium rounded-lg transition-colors"
                      >
                        {t('settings.disconnectWallet')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Wallet già connesso e associato: messaggio chiaro, nessuna richiesta di riconnessione */}
            {isConnected && account && associatedWallet && (account.toLowerCase() === associatedWallet.toLowerCase()) && (
              <div className="mb-4 p-3 bg-green-900/30 border border-green-500/50 rounded-lg">
                <p className="text-green-400 text-sm font-medium">{t('settings.walletConnected')} • {t('settingsExtra.walletAssociatedLabel')}</p>
                <p className="text-gray-400 text-xs mt-1">{t('settingsExtra.walletConnectedReadyInfo')}</p>
              </div>
            )}

            {/* Mostra wallet connesso e pulsante Salva (solo se connesso ma non ancora associato) */}
            {isConnected && account && !associatedWallet && (
              <div className="space-y-2 mb-4">
                <p className="text-gray-300 text-sm">{t('settingsExtra.walletConnectedLabel')}</p>
                <div className="bg-gray-800 p-3 rounded-lg mb-4">
                  <p className="text-yellow-400 font-mono text-sm break-all">{account}</p>
                </div>
                <button
                  onClick={handleSaveWallet}
                  disabled={savingWallet}
                  className="btn-football w-full mb-4"
                >
                  {savingWallet ? t('settingsExtra.savingWallet') : t('settingsExtra.saveWallet')}
                </button>
              </div>
            )}

            {/* Hint for mobile: show only on small screens */}
            {!isConnected && (
              <p className="text-gray-400 text-sm mb-3 md:hidden">
                {t('settingsExtra.walletMobileHint')}
              </p>
            )}
            {/* Connect solo se wallet NON connesso; se già connesso mostriamo stato + Disconnetti */}
            <div className="space-y-2">
              {isConnected && account ? (
                <>
                  <button
                    type="button"
                    disabled
                    className="btn-football w-full flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-600 cursor-default min-h-[48px] py-3"
                  >
                    <Wallet className="w-5 h-5 flex-shrink-0" />
                    <span>{t('settings.walletConnected')}</span>
                  </button>
                  {disconnectWallet && (
                    <button
                      type="button"
                      onClick={() => disconnectWallet(false)}
                      className="w-full py-2.5 rounded-lg border border-gray-500 text-gray-300 hover:bg-gray-700 hover:border-gray-400 transition-colors text-sm font-medium"
                    >
                      {t('settings.disconnectWallet')}
                    </button>
                  )}
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleConnectWallet}
                  disabled={connecting}
                  className="btn-football w-full flex items-center justify-center space-x-2 min-h-[48px] py-3 touch-manipulation"
                  style={{ minHeight: '48px' }}
                >
                  <Wallet className="w-5 h-5 flex-shrink-0" />
                  <span>{connecting ? t('settings.connecting') : t('settings.connectWallet')}</span>
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-gray-300 mb-2">{t('settings.languageLabel')}</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="input-football w-full"
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>{lang.name}</option>
              ))}
            </select>
          </div>
          <div className="profile-setting-toggle">
            <label className="profile-setting-toggle-label flex items-center space-x-2">
              <input
                type="checkbox"
                checked={tutorialCompleted}
                onChange={(e) => setTutorialCompleted(e.target.checked)}
                className="profile-setting-toggle-input w-4 h-4"
              />
              <span className="profile-setting-toggle-text text-gray-300">{t('settings.tutorialCompleted')}</span>
            </label>
          </div>
          <div className="profile-setting-toggle">
            <label className="profile-setting-toggle-label flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={soundsEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setSoundsEnabled(enabled);
                  if (enabled) playSound('click');
                }}
                className="profile-setting-toggle-input w-4 h-4"
              />
              <span className="profile-setting-toggle-text text-gray-300">{t('settings.soundsEnabled')}</span>
            </label>
            <p className="profile-setting-toggle-description text-gray-500 text-sm mt-1 ml-6">{t('settings.soundsEnabledDesc')}</p>
          </div>
          <div className="profile-setting-toggle">
            <label className="profile-setting-toggle-label flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={musicEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setMusicEnabled(enabled);
                  if (enabled) playSound('click');
                }}
                className="profile-setting-toggle-input w-4 h-4"
              />
              <span className="profile-setting-toggle-text text-gray-300">{t('settings.musicEnabled')}</span>
            </label>
            <p className="profile-setting-toggle-description text-gray-500 text-sm mt-1 ml-6">{t('settings.musicEnabledDesc')}</p>
            {musicEnabled && !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && (
              <div className="mt-3 ml-6">
                <label className="block text-gray-300 text-sm mb-1">{t('settings.musicVolume')}: {musicVolumePercent}%</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={musicVolumePercent}
                  onChange={(e) => setMusicVolume(parseInt(e.target.value, 10) / 100)}
                  onInput={(e) => setMusicVolume(parseInt(e.target.value, 10) / 100)}
                  className="music-volume-slider w-full max-w-xs h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-yellow-500"
                />
              </div>
            )}
          </div>
          <div className="border-t border-gray-700 pt-6 mt-6 space-y-4">
            <h3 className="text-lg font-semibold text-[#1e293b]">{t('settings.passwordSectionTitle')}</h3>
            <div>
              <label className="block text-gray-300 mb-2">{t('settings.currentPassword')}</label>
              <input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => handlePasswordFieldChange('currentPassword', e.target.value)}
                className="input-football w-full"
                autoComplete="current-password"
                placeholder={t('settings.currentPasswordPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">{t('settings.newPassword')}</label>
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => handlePasswordFieldChange('newPassword', e.target.value)}
                className="input-football w-full"
                autoComplete="new-password"
                placeholder={t('settings.newPasswordPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">{t('settings.confirmNewPassword')}</label>
              <input
                type="password"
                value={passwordForm.confirmNewPassword}
                onChange={(e) => handlePasswordFieldChange('confirmNewPassword', e.target.value)}
                className="input-football w-full"
                autoComplete="new-password"
                placeholder={t('settings.confirmNewPasswordPlaceholder')}
              />
            </div>
            <button
              type="button"
              className="btn-football"
              onClick={() => { playSound('click'); handleChangePassword(); }}
              disabled={savingPassword}
            >
              {savingPassword ? t('settings.changingPassword') : t('settings.changePassword')}
            </button>
          </div>
          <button
            className="btn-football"
            onClick={() => { playSound('click'); handleSaveSettings(); }}
            disabled={savingSettings}
          >
            {savingSettings ? t('common.saving') : t('common.save')}
          </button>

          {/* Privacy & Cookies */}
          <div className="border-t border-gray-700 pt-6 mt-6">
            <h3 className="text-lg font-semibold text-[#1e293b] mb-2">{t('gdpr.bannerTitle')}</h3>
            <p className="text-gray-500 text-sm mb-3">{t('gdpr.preferencesIntro')}</p>
            <button
              type="button"
              onClick={() => { playSound('click'); openCookiePreferences(); }}
              className="px-4 py-2 text-sm font-medium text-[#4169E1] hover:bg-[#4169E1]/10 border border-[#4169E1]/30 transition-colors"
            >
              {t('gdpr.cookiePreferencesLink')}
            </button>
          </div>
          
          {/* Logout Section */}
          <div className="border-t border-gray-700 pt-6 mt-6">
            <h3 className="text-lg font-semibold text-[#1e293b] mb-4">{t('settings.accountTitle')}</h3>
            <button
              onClick={() => { playSound('click'); handleLogout(); }}
              className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all flex items-center justify-center space-x-2"
            >
              <span>🚪</span>
              <span>{t('settings.logout')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Modal per chiedere se salvare il wallet - mobile friendly */}
      {showWalletModal && account && (
        <div
          className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-4 sm:p-4"
          style={{
            paddingLeft: 'max(1rem, env(safe-area-inset-left))',
            paddingRight: 'max(1rem, env(safe-area-inset-right))',
            paddingBottom: 'max(1rem, env(safe-area-inset-bottom))'
          }}
        >
          <div className="football-card glass-football p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-[#1e293b] mb-4">{t('settingsExtra.walletModalTitle')}</h2>
            <div className="mb-4">
              <p className="text-gray-300 mb-2">{t('settingsExtra.walletModalConnectedSuccess')}</p>
              <div className="bg-gray-800 p-3 rounded-lg mb-4">
                <p className="text-yellow-400 font-mono text-sm break-all">{account}</p>
              </div>
              <p className="text-gray-300 text-sm">
                {t('settingsExtra.walletModalQuestion')}
              </p>
            </div>
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:space-x-4">
              <button
                onClick={handleSkipSaveWallet}
                disabled={savingWallet}
                className="px-6 py-3 min-h-[48px] border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all flex-1 touch-manipulation"
              >
                {t('settingsExtra.walletModalSkip')}
              </button>
              <button
                onClick={handleSaveWallet}
                disabled={savingWallet}
                className="btn-football flex-1 min-h-[48px] py-3 touch-manipulation"
              >
                {savingWallet ? t('settingsExtra.savingWallet') : t('settingsExtra.saveWalletConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SettingsTab;
